import { type RefObject, useEffect, useRef, useState } from 'react';
import type { Team } from '@cs-shot/battle-sim';
import { playSfx } from './sfx';
import { ROUND_ANIM } from '../render/battleEngine';
import type { BattleEngine } from './useBattleCanvas';

export interface RoundRevealParams {
  won: boolean;
  winner: Team | null;
  yourSide: Team | undefined;
  onFinished: (won: boolean) => void;
}

/**
 * On the final (over) update: replay the last round's combat FIRST, then reveal the
 * winning-team banner (+ victory jingle) the moment the replay actually ends — not after a
 * fixed cap — then hand off to the result screen. A hard ceiling (ROUND_ANIM) is the fallback
 * if the engine never reaches the end. Returns whether the banner should show.
 */
export function useRoundReveal(
  engineRef: RefObject<BattleEngine | null>,
  over: boolean,
  round: number,
  params: RoundRevealParams,
): boolean {
  const [showResult, setShowResult] = useState(false);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    if (!over) { setShowResult(false); return; }
    const { won, winner, yourSide, onFinished } = paramsRef.current;
    const youWon = winner != null && winner === yourSide;
    let finish = 0;
    const reveal = () => {
      setShowResult(true);
      // CS-style winning-side announcer ("Counter-Terrorists Win!" / "Terrorists Win!")
      if (winner) playSfx(winner === 'ct' ? 'ct_win' : 't_win', 0.8);
      playSfx(youWon ? 'match_win' : 'match_lose', 0.6);
      finish = window.setTimeout(() => onFinished(won), 1800);
    };
    let revealed = false;
    const fire = () => { if (revealed) return; revealed = true; window.clearInterval(poll); reveal(); };
    const poll = window.setInterval(() => {
      const engine = engineRef.current;
      if (!engine || engine.getActiveRound() !== round) return;
      if (performance.now() / 1000 - engine.getActiveAt() >= engine.getReplayDur()) fire();
    }, 100);
    const ceiling = window.setTimeout(fire, ROUND_ANIM * 1000); // fallback if engine never arrives
    return () => { window.clearInterval(poll); window.clearTimeout(ceiling); if (finish) window.clearTimeout(finish); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, round]);

  return showResult;
}
